import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { mobilePrintPdf } from "@/lib/mobilePrint";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, RefreshCw, Printer, PackageCheck, FileSpreadsheet } from "lucide-react";
import DTDCExportDialog from "@/components/DTDCExportDialog";
import CourierStatusDialog from "@/components/CourierStatusDialog";
import { supportsLiveTracking } from "@/lib/courierTracking";

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-800",
  packaging: "bg-yellow-100 text-yellow-800",
  packed: "bg-green-100 text-green-800",
  dispatched: "bg-purple-100 text-purple-800",
};
const CHECK_COLORS = {
  received: "bg-green-100 text-green-800",
  pending: "bg-yellow-100 text-yellow-800",
  pending_recheck: "bg-red-100 text-red-800",
};
const CHECK_LABELS = { received: "Checked", pending: "Pending", pending_recheck: "Re-check" };
const COURIER_OPTIONS = ["DTDC", "Anjani", "India Post", "Amazon", "Shiprocket", "Delhivery", "Delhivery B2B", "Others"];

export default function AllOrders() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(sp.get("q") || "");
const [searchDebounced, setSearchDebounced] = useState(sp.get("q") || "");
const [statusFilter, setStatusFilter] = useState(sp.get("status") || "all");
const [payStatusFilter, setPayStatusFilter] = useState(sp.get("pay") || "all");
const [checkStatusFilter, setCheckStatusFilter] = useState(sp.get("check") || "all");
const [shippingFilter, setShippingFilter] = useState(sp.get("shipping") || "all");
const [courierFilter, setCourierFilter] = useState(sp.get("courier") || "all");
const [readyToBook, setReadyToBook] = useState(sp.get("rtb") === "true");
const [periodFilter, setPeriodFilter] = useState(sp.get("period") || "all");
const [dateFrom, setDateFrom] = useState(sp.get("from") || "");
const [dateTo, setDateTo] = useState(sp.get("to") || "");
const [viewAll, setViewAll] = useState(sp.get("viewAll") === "true");
  const [users, setUsers] = useState([]);
  const [selectedTelecaller, setSelectedTelecaller] = useState(sp.get("tc") || "all");
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [printLoading, setPrintLoading] = useState(false);
  const [printQuantities, setPrintQuantities] = useState({});
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [showDTDCDialog, setShowDTDCDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(Number(sp.get("page")) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageInput, setPageInput] = useState("");
  const [totalOrders, setTotalOrders] = useState(0);
  const PAGE_SIZE = 50;

  // Sync filters to URL search params
  useEffect(() => {
    const p = new URLSearchParams();
    if (statusFilter !== "all") p.set("status", statusFilter);
    if (payStatusFilter !== "all") p.set("pay", payStatusFilter);
    if (checkStatusFilter !== "all") p.set("check", checkStatusFilter);
    if (periodFilter !== "all") p.set("period", periodFilter);
    if (periodFilter === "custom" && dateFrom) p.set("from", dateFrom);
    if (periodFilter === "custom" && dateTo) p.set("to", dateTo);
    if (shippingFilter !== "all") p.set("shipping", shippingFilter);
    if (shippingFilter === "courier" && courierFilter !== "all") p.set("courier", courierFilter);
    if (readyToBook) p.set("rtb", "true");
    if (viewAll) p.set("viewAll", "true");
    if (selectedTelecaller !== "all") p.set("tc", selectedTelecaller);
    if (searchDebounced) p.set("q", searchDebounced);
    if (currentPage > 1) p.set("page", currentPage.toString());
    setSp(p, { replace: true });
  }, [statusFilter, payStatusFilter, checkStatusFilter, periodFilter, dateFrom, dateTo, shippingFilter, courierFilter, readyToBook, viewAll, selectedTelecaller, searchDebounced, currentPage]);

  const canPrintAddresses = user?.role === "admin" || user?.role === "packaging" || user?.role === "accounts";
  const canPrintPackingSheets = user?.role === "admin" || user?.role === "packaging" || user?.role === "accounts";
  const showPaymentCheck = ["admin", "telecaller", "accounts"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  // Physical dispatch slip receipt — same field the Accounts dashboard writes,
  // so ticking it anywhere stays in sync everywhere. Accounts always see the
  // column; admins switch it on when they need it (off by default).
  const [showSlipCol, setShowSlipCol] = useState(false);
  const showSlip = user?.role === "accounts" || (user?.role === "admin" && showSlipCol);
  const [slipUpdating, setSlipUpdating] = useState({});

  const updateSlipReceived = async (orderId, received) => {
    setSlipUpdating(p => ({ ...p, [orderId]: true }));
    try {
      await api.put(`/orders/${orderId}/slip-received`, { slip_received: received });
      setOrders(prev => prev.map(o => o.id === orderId
        ? { ...o, slip_received: received, slip_received_by: received ? user?.name : "" }
        : o));
    } catch {
      toast.error("Failed to update slip status");
    } finally {
      setSlipUpdating(p => ({ ...p, [orderId]: false }));
    }
  };

  const toggleForwardToPackaging = async (orderId, e) => {
    e.stopPropagation();
    try {
      const res = await api.post(`/orders/${orderId}/forward-to-packaging`);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, forwarded_to_packaging: res.data.forwarded_to_packaging } : o));
    } catch { toast.error("Failed to update"); }
  };

  useEffect(() => {
    loadOrders();
    if (user?.role === "admin") loadUsers();
  }, [viewAll, selectedTelecaller, currentPage, searchDebounced, statusFilter, payStatusFilter, checkStatusFilter, shippingFilter, courierFilter, readyToBook, periodFilter, dateFrom, dateTo]);

  useEffect(() => {
    const timer = setTimeout(() => { setSearchDebounced(search); setCurrentPage(1); }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const loadUsers = async () => {
    try { const res = await api.get("/users"); setUsers(res.data.filter(u => u.role === "telecaller" || u.role === "admin")); }
    catch { }
  };

  const loadOrders = async () => {
    setLoading(true);
    setSelectedOrders(new Set());
    try {
      const params = new URLSearchParams();
      if (user?.role === "telecaller") {
        params.set("view_all", viewAll ? "true" : "false");
      } else {
        params.set("view_all", "true");
        if (user?.role === "admin" && selectedTelecaller !== "all") params.set("telecaller_id", selectedTelecaller);
      }
      params.set("page", currentPage.toString());
      params.set("page_size", PAGE_SIZE.toString());
      if (searchDebounced) params.set("search", searchDebounced);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (payStatusFilter !== "all") params.set("payment_status", payStatusFilter);
      if (checkStatusFilter !== "all") params.set("check_status", checkStatusFilter);
      if (shippingFilter !== "all") params.set("shipping_method", shippingFilter);
      if (shippingFilter === "courier" && courierFilter !== "all") params.set("courier_name", courierFilter);
      if (readyToBook) params.set("ready_to_book", "true");
      if (periodFilter !== "all") params.set("period", periodFilter);
      if (periodFilter === "custom" && dateFrom) params.set("date_from", dateFrom);
      if (periodFilter === "custom" && dateTo) params.set("date_to", dateTo);
      const res = await api.get(`/orders?${params.toString()}`);
      const data = res.data;
      setOrders(data.orders || []);
      setTotalPages(data.total_pages || 1);
      setTotalOrders(data.total || 0);
    } catch { } finally { setLoading(false); }
  };

  // Server handles all filtering — use orders directly
  const filteredOrders = orders;

  const toggleSelect = (id) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedOrders.size === filteredOrders.length) setSelectedOrders(new Set());
    else setSelectedOrders(new Set(filteredOrders.map(o => o.id)));
  };

  const handlePrintAddresses = async () => {
    setPrintLoading(true);
    try {
      const res = await api.post("/orders/print-addresses", { order_ids: [...selectedOrders], quantities: printQuantities }, { responseType: "blob" });
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isMobile) {
        mobilePrintPdf(new Blob([res.data], { type: "application/pdf" }), "addresses.pdf");
      } else {
        const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.top = "-10000px";
        iframe.style.left = "-10000px";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.src = blobUrl;
        document.body.appendChild(iframe);
        iframe.onload = () => { setTimeout(() => { iframe.contentWindow.print(); }, 500); };
      }
      setShowPrintDialog(false);
    } catch { toast.error("Failed to generate address PDF"); }
    finally { setPrintLoading(false); }
  };

  const handlePrintPackingSheets = async () => {
    setPrintLoading(true);
    try {
      const res = await api.post("/orders/print-packing-sheets", { order_ids: [...selectedOrders] }, { responseType: "blob" });
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isMobile) {
        mobilePrintPdf(new Blob([res.data], { type: "application/pdf" }), "bulk_packing_sheets.pdf");
      } else {
        const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.top = "-10000px";
        iframe.style.left = "-10000px";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.src = blobUrl;
        document.body.appendChild(iframe);
        iframe.onload = () => { setTimeout(() => { iframe.contentWindow.print(); }, 500); };
      }
    } catch { toast.error("Failed to generate packing sheets PDF"); }
    finally { setPrintLoading(false); }
  };

  const openPrintDialog = () => {
    if (selectedOrders.size === 0) return toast.error("Select at least one order");
    // Initialize quantities to 1 for all selected
    const q = {};
    selectedOrders.forEach(id => { q[id] = printQuantities[id] || 1; });
    setPrintQuantities(q);
    setShowPrintDialog(true);
  };

  // Get selected DTDC orders for export
  const selectedDTDCOrders = orders.filter(
    o => selectedOrders.has(o.id) && o.courier_name === "DTDC"
  );

  const handleDTDCExport = () => {
    if (selectedDTDCOrders.length === 0) {
      toast.error("No DTDC orders selected. Select DTDC courier orders to export.");
      return;
    }
    setShowDTDCDialog(true);
  };

  return (
    <div className="space-y-6" data-testid="all-orders-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">All Orders</h1>
        <div className="flex items-center gap-2">
          {canPrintAddresses && selectedOrders.size > 0 && (
            <Button variant="default" size="sm" onClick={openPrintDialog} disabled={printLoading} data-testid="print-addresses-btn">
              <Printer className="w-4 h-4 mr-1" />
              {printLoading ? "Generating..." : `Print Addresses (${selectedOrders.size})`}
            </Button>
          )}
          {canPrintPackingSheets && selectedOrders.size > 0 && (
            <Button variant="default" size="sm" onClick={handlePrintPackingSheets} disabled={printLoading} data-testid="print-packing-sheets-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5">
              <Printer className="w-4 h-4" />
              {printLoading ? "Generating..." : `Print Packing Sheets (${selectedOrders.size})`}
            </Button>
          )}
          {selectedOrders.size > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDTDCExport}
              data-testid="dtdc-export-btn"
              className="border-green-500 text-green-700 hover:bg-green-50 gap-1.5"
              title={selectedDTDCOrders.length > 0 ? `Export ${selectedDTDCOrders.length} DTDC order(s)` : "Select DTDC orders to export"}
            >
              <FileSpreadsheet className="w-4 h-4" />
              DTDC Excel
              {selectedDTDCOrders.length > 0 && (
                <span className="ml-0.5 bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {selectedDTDCOrders.length}
                </span>
              )}
            </Button>
          )}
          <Button
            variant={readyToBook ? "default" : "outline"}
            size="sm"
            onClick={() => { setReadyToBook(v => !v); setCurrentPage(1); }}
            className={readyToBook ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
            data-testid="ready-to-book-filter"
            title="Show only orders the packing team has weighed and released for booking"
          >
            <PackageCheck className="w-4 h-4 mr-1" />Ready to Book
          </Button>
          <Button variant="outline" size="sm" onClick={loadOrders} data-testid="refresh-orders"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          {/* Filter row */}
          <div className="flex flex-wrap gap-2 items-end">
            <div className="relative flex-1 min-w-[140px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search by order #, customer, alias, phone, GST, city, state, LR..." className="pl-9 h-8 text-sm" value={search} onChange={e => setSearch(e.target.value)} data-testid="orders-search-input" />
            </div>
            {/* Period */}
            <Select value={periodFilter} onValueChange={(v) => { setPeriodFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-28 sm:w-32 h-8 text-xs" data-testid="period-filter"><SelectValue placeholder="Period" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            {periodFilter === "custom" && (
              <>
                <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setCurrentPage(1); }} className="w-36 h-8 text-xs" />
                <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setCurrentPage(1); }} className="w-36 h-8 text-xs" />
              </>
            )}
            {/* Order status */}
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="status-filter-select"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="yet_to_dispatch">Yet to dispatch</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="packaging">Packaging</SelectItem>
                <SelectItem value="packed">Packed</SelectItem>
                <SelectItem value="dispatched">Dispatched</SelectItem>
              </SelectContent>
            </Select>
            {/* Shipping filter */}
            <Select
              value={shippingFilter}
              onValueChange={(v) => {
                setShippingFilter(v);
                if (v !== "courier") setCourierFilter("all");
              }}
            >
              <SelectTrigger className="w-36 h-8 text-xs" data-testid="shipping-filter"><SelectValue placeholder="Shipping" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Shipping</SelectItem>
                <SelectItem value="courier">Courier</SelectItem>
                <SelectItem value="transport">Transport</SelectItem>
                <SelectItem value="porter">Porter</SelectItem>
                <SelectItem value="office_collection">Office Collection</SelectItem>
                <SelectItem value="self_arranged">Self Arranged Shipping</SelectItem>
              </SelectContent>
            </Select>
            {shippingFilter === "courier" && (
              <Select value={courierFilter} onValueChange={setCourierFilter}>
                <SelectTrigger className="w-36 h-8 text-xs" data-testid="courier-filter"><SelectValue placeholder="Courier Name" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Couriers</SelectItem>
                  {COURIER_OPTIONS.map((courier) => (
                    <SelectItem key={courier} value={courier}>{courier}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {/* Payment status */}
            <Select value={payStatusFilter} onValueChange={(v) => { setPayStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid="pay-status-filter"><SelectValue placeholder="Payment" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payments</SelectItem>
                <SelectItem value="full">Fully Paid</SelectItem>
                <SelectItem value="partial">Partially Paid</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
              </SelectContent>
            </Select>
            {/* Check status — visible to admin/telecaller/accounts */}
            {showPaymentCheck && (
              <Select value={checkStatusFilter} onValueChange={(v) => { setCheckStatusFilter(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-36 h-8 text-xs" data-testid="check-status-filter"><SelectValue placeholder="Check Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Checks</SelectItem>
                  <SelectItem value="received">Checked</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="pending_recheck">Pending Re-check</SelectItem>
                </SelectContent>
              </Select>
            )}
            {/* Admin telecaller filter */}
            {user?.role === "admin" && (
              <Select value={selectedTelecaller} onValueChange={setSelectedTelecaller}>
                <SelectTrigger className="w-40 h-8 text-xs" data-testid="executive-filter-select"><SelectValue placeholder="All Executives" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Executives</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {user?.role === "telecaller" && (
              <div className="flex items-center gap-2">
                <Checkbox id="viewAllOrders" checked={viewAll} onCheckedChange={setViewAll} data-testid="view-all-toggle" />
                <Label htmlFor="viewAllOrders" className="cursor-pointer text-sm whitespace-nowrap">Show All</Label>
              </div>
            )}
            {/* Admins opt in to the slip column; accounts always see it. */}
            {isAdmin && (
              <div className="flex items-center gap-2">
                <Checkbox id="showSlipCol" checked={showSlipCol} onCheckedChange={(v) => setShowSlipCol(!!v)} data-testid="slip-column-toggle" />
                <Label htmlFor="showSlipCol" className="cursor-pointer text-sm whitespace-nowrap">Slip Rcvd</Label>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div></div>
          ) : filteredOrders.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No orders found</p>
          ) : (
              <Table className="min-w-[700px]">
                <TableHeader>
                  <TableRow>
                    {canPrintAddresses && (
                      <TableHead className="w-10">
                        <Checkbox checked={filteredOrders.length > 0 && selectedOrders.size === filteredOrders.length}
                          onCheckedChange={toggleSelectAll} data-testid="select-all-orders-checkbox" />
                      </TableHead>
                    )}
                    <TableHead className="text-xs uppercase whitespace-nowrap">Order #</TableHead>
                    <TableHead className="text-xs uppercase whitespace-nowrap">Customer</TableHead>
                    <TableHead className="text-xs uppercase whitespace-nowrap">Amount</TableHead>
                    <TableHead className="text-xs uppercase whitespace-nowrap">Status</TableHead>
                    <TableHead className="text-xs uppercase whitespace-nowrap">Payment</TableHead>
                    {user?.role === "accounts" && <TableHead className="text-xs uppercase whitespace-nowrap">GST</TableHead>}
                    {user?.role !== "accounts" && <TableHead className="text-xs uppercase whitespace-nowrap">Invoice</TableHead>}
                    {showPaymentCheck && <TableHead className="text-xs uppercase whitespace-nowrap">Check</TableHead>}
                    {user?.role === "admin" && <TableHead className="text-xs uppercase whitespace-nowrap">Executive</TableHead>}
                    <TableHead className="text-xs uppercase whitespace-nowrap">Date</TableHead>
                    <TableHead className="text-xs uppercase whitespace-nowrap">Shipping</TableHead>
                    {showSlip && <TableHead className="text-xs uppercase whitespace-nowrap">Slip Rcvd</TableHead>}
                    {isAdmin && <TableHead className="text-xs uppercase whitespace-nowrap">Fwd Pkg</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map(o => {
                    const checkStatus = o.payment_check_status || "pending";
                    return (
                      <TableRow key={o.id} data-testid={`order-row-${o.id}`} className={selectedOrders.has(o.id) ? "bg-primary/5" : ""}>
                        {canPrintAddresses && (
                          <TableCell>
                            <Checkbox checked={selectedOrders.has(o.id)} onCheckedChange={() => toggleSelect(o.id)} data-testid={`select-order-${o.id}`} />
                          </TableCell>
                        )}
                        <TableCell>
                          <Link to={`/orders/${o.id}`} className="font-mono text-sm font-medium text-primary hover:underline" data-testid={`order-link-${o.id}`}>
                            {o.order_number}
                          </Link>
                        </TableCell>
                        <TableCell className="text-sm">{o.customer_name}{o.customer_alias ? <span className="text-xs text-muted-foreground ml-1">({o.customer_alias})</span> : ""}</TableCell>
                        <TableCell className="text-sm font-mono whitespace-nowrap">{"\u20B9"}{o.grand_total?.toLocaleString("en-IN")}</TableCell>
                        <TableCell><Badge className={`${STATUS_COLORS[o.status] || "bg-gray-100"} text-xs`}>{o.status}</Badge></TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{o.payment_status}</Badge></TableCell>
                        {user?.role === "accounts" && (
                          <TableCell><Badge variant="outline" className={`text-xs ${o.gst_applicable ? "border-blue-300 text-blue-700" : "border-gray-300 text-gray-500"}`} data-testid={`gst-badge-${o.id}`}>{o.gst_applicable ? "GST" : "Non-GST"}</Badge></TableCell>
                        )}
                        {user?.role !== "accounts" && (
                          <TableCell>
                            {o.gst_applicable ? (
                              <Badge className={`text-xs ${o.tax_invoice_url ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`} data-testid={`invoice-status-${o.id}`}>
                                {o.tax_invoice_url ? "Uploaded" : "Pending"}
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground" data-testid={`invoice-status-${o.id}`}>Not Required</span>
                            )}
                          </TableCell>
                        )}
                        {showPaymentCheck && (
                          <TableCell>
                            <Badge className={`${CHECK_COLORS[checkStatus] || "bg-gray-100"} text-xs`} data-testid={`check-badge-${o.id}`}>
                              {CHECK_LABELS[checkStatus] || "Pending"}
                            </Badge>
                          </TableCell>
                        )}
                        {user?.role === "admin" && <TableCell className="text-sm whitespace-nowrap">{o.telecaller_name || "-"}</TableCell>}
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{new Date(o.created_at).toLocaleDateString("en-IN")}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap" data-testid={`shipping-method-${o.id}`}>
                          <div className="flex items-center gap-1.5">
                            <span>{o.shipping_method === "courier" ? (o.courier_name || "Courier") : o.shipping_method === "transport" ? (o.transporter_name || "Transport") : o.shipping_method || "-"}</span>
                            {o.packaging?.ready_to_book && o.status !== "dispatched" && (
                              <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px] px-1.5 py-0" title={`Weighed: ${o.packaging?.weight_kg || "?"} KG · ${o.packaging?.num_boxes || 1} box(es)`}>
                                Ready to Book
                              </Badge>
                            )}
                            {/* Live status is only meaningful once a docket exists */}
                            {supportsLiveTracking(o.dispatch?.courier_name || o.courier_name) && (o.dispatch?.lr_no || o.dtdc_shipment?.awb || o.amazon_shipment?.tracking_id) && (
                              <CourierStatusDialog
                                orderId={o.id}
                                orderNumber={o.order_number}
                                courier={o.courier_name}
                                docket={o.dispatch?.lr_no || o.dtdc_shipment?.awb || o.amazon_shipment?.tracking_id}
                              />
                            )}
                          </div>
                        </TableCell>
                        {showSlip && (
                          <TableCell data-testid={`slip-cell-${o.id}`}>
                            {["courier", "transport"].includes(o.shipping_method) ? (
                              <div className="flex items-center gap-1.5">
                                <Checkbox
                                  checked={!!o.slip_received}
                                  onCheckedChange={(v) => updateSlipReceived(o.id, !!v)}
                                  disabled={!!slipUpdating[o.id]}
                                  data-testid={`slip-checkbox-${o.id}`}
                                  aria-label="Physical dispatch slip received"
                                />
                                {o.slip_received && o.slip_received_by && (
                                  <span className="text-[10px] text-muted-foreground truncate max-w-[70px]" title={`Received by ${o.slip_received_by}`}>
                                    {o.slip_received_by}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        )}
                        {isAdmin && (
                          <TableCell>
                            <Button
                              variant={o.forwarded_to_packaging ? "default" : "outline"}
                              size="icon"
                              className={`h-7 w-7 ${o.forwarded_to_packaging ? "bg-green-600 hover:bg-green-700" : ""}`}
                              onClick={(e) => toggleForwardToPackaging(o.id, e)}
                              data-testid={`fwd-pkg-${o.id}`}
                              title={o.forwarded_to_packaging ? "Forwarded to Packaging" : "Forward to Packaging"}
                            >
                              <PackageCheck className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
          )}
          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <span>{selectedOrders.size > 0 ? `${selectedOrders.size} selected · ` : ""}{totalOrders} total order(s)</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-7" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} data-testid="prev-page-btn">Previous</Button>
              <span className="text-xs">Page {currentPage} of {totalPages}</span>
              {totalPages > 1 && (
                <form className="flex items-center gap-1"
                      onSubmit={(e) => {
                        e.preventDefault();
                        // Clamp rather than reject, so 999 lands on the last page.
                        const n = parseInt(pageInput, 10);
                        if (!n || n < 1) return;
                        setCurrentPage(Math.min(n, totalPages));
                        setPageInput("");
                      }}>
                  <span className="text-xs">Go to</span>
                  <Input value={pageInput}
                         onChange={(e) => setPageInput(e.target.value.replace(/\D/g, ""))}
                         placeholder="#" inputMode="numeric"
                         className="h-7 w-14 text-xs text-center"
                         data-testid="goto-page-input" />
                  <Button type="submit" variant="outline" size="sm" className="h-7"
                          disabled={!pageInput} data-testid="goto-page-btn">Go</Button>
                </form>
              )}
              <Button variant="outline" size="sm" className="h-7" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} data-testid="next-page-btn">Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Print Address Quantities Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Print Addresses — Set Copies</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {[...selectedOrders].map(oid => {
              const o = orders.find(x => x.id === oid);
              return o ? (
                <div key={oid} className="flex items-center justify-between gap-3 py-1.5 px-2 rounded border">
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium">{o.order_number}</span>
                    <span className="text-xs text-muted-foreground ml-2">{o.customer_name}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Label className="text-xs">Copies:</Label>
                    <Input type="number" min={1} max={10} className="w-16 h-7 text-sm text-center"
                      value={printQuantities[oid] || 1}
                      onChange={e => setPrintQuantities(prev => ({ ...prev, [oid]: Math.max(1, parseInt(e.target.value) || 1) }))}
                      data-testid={`print-qty-${oid}`} />
                  </div>
                </div>
              ) : null;
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPrintDialog(false)}>Cancel</Button>
            <Button onClick={handlePrintAddresses} disabled={printLoading} data-testid="confirm-print-btn">
              <Printer className="w-4 h-4 mr-1" />
              {printLoading ? "Generating..." : "Print"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DTDC Excel Export Dialog */}
      <DTDCExportDialog
        open={showDTDCDialog}
        onClose={() => setShowDTDCDialog(false)}
        orders={selectedDTDCOrders}
      />
    </div>
  );
}
